const MAX_PHOTO_SIDE = 2000

export const isPdf = (file) => file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '')

// pdf.js is only loaded when someone picks a PDF, so it doesn't slow down the rest of the site.
// It runs on the page itself rather than in a background worker, which is fine for a few pages.
const loadPdfJs = async () => {
  await import('pdfjs-dist/legacy/build/pdf.worker.entry')
  return import('pdfjs-dist/legacy/build/pdf')
}

const canvasToJpeg = (canvas) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not save the page'))),
      'image/jpeg',
      0.85
    )
  )

// Turn the first pages of a PDF into JPEG pictures, the same as a shrunk phone photo,
// so a PDF is read and saved exactly like photos of the card.
// Returns the pictures and how many pages the PDF has.
const pdfToPhotos = async (file, maxPages) => {
  const pdfjs = await loadPdfJs()
  const pdf = await pdfjs.getDocument({
    data: await file.arrayBuffer(),
    // Never run code from inside the PDF (CVE-2024-4367)
    isEvalSupported: false,
  }).promise

  try {
    const photos = []
    for (let number = 1; number <= Math.min(pdf.numPages, maxPages); number++) {
      const page = await pdf.getPage(number)
      const size = page.getViewport({ scale: 1 })
      const scale = MAX_PHOTO_SIDE / Math.max(size.width, size.height)
      const viewport = page.getViewport({ scale })

      const canvas = document.createElement('canvas')
      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      const context = canvas.getContext('2d')
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      // 'print' draws the whole page in one go. The normal on-screen mode waits for the
      // browser to paint, which stops while the tab is in the background.
      await page.render({ canvasContext: context, viewport, intent: 'print' }).promise

      photos.push(await canvasToJpeg(canvas))
      page.cleanup()
    }
    return { photos, pageCount: pdf.numPages }
  } finally {
    pdf.destroy()
  }
}

export default pdfToPhotos
